/**
 * Method Selector Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 *
 * Handles method selection UI with categorization and search
 */
import React from "react";

import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";

import { MethodDefinition } from "../types";

interface MethodSelectorProps {
  methods: MethodDefinition[];
  selectedMethod: string;
  onMethodChange: (methodName: string) => void;
  loading?: boolean;
  error?: string | null;
}

export const MethodSelector: React.FC<MethodSelectorProps> = ({
  methods,
  selectedMethod,
  onMethodChange,
  loading = false,
  error = null,
}) => {
  const selectedMethodDef = methods.find((m) => m.name === selectedMethod);

  return (
    <Box sx={{ flexGrow: 1, minWidth: 300 }}>
      <Autocomplete
        options={methods}
        getOptionLabel={(option) => option.name}
        value={selectedMethodDef || null}
        onChange={(_, newValue) => onMethodChange(newValue?.name || "")}
        loading={loading}
        disabled={loading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Method"
            size="small"
            error={!!error}
            helperText={error}
            variant="outlined"
            sx={{
              "& .MuiInputBase-root": {
                py: 0.5,
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              py: 0.5,
              minHeight: "auto",
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "medium", lineHeight: 1.2 }}
              >
                {option.name}
              </Typography>
              {option.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1 }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-option": {
              py: 0.5,
              minHeight: "auto",
            },
          },
        }}
      />

      {selectedMethodDef?.deprecated && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label="Deprecated"
            size="small"
            color="warning"
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};
